/**
 * The audience join URL for a Room — the address behind the join QR.
 *
 * Pure and import-free so it can be unit-tested from the backend project (which
 * has no `@showliner/shared` path mapping) and so the one place that knows the
 * URL's SHAPE is not also the place that decides which host to use.
 *
 * Those are deliberately different questions. The shape is fixed everywhere;
 * the origin is not — the venue display is served BY the audience app and so
 * defaults to its own origin, while the Operator console runs on the admin
 * origin and must resolve the live one (`getLiveBaseUrl`). Folding the two
 * together is how the console's preview QR ended up encoding
 * `admin.showliner.io/?roomCode=…`.
 */

/**
 * Build the URL a fan lands on when they scan a room's join code.
 *
 * `?roomCode=` is what `Landing.tsx` reads to auto-join — it mints a friendly
 * username and drops the fan straight into the room with no prompt — so this
 * query parameter is a contract with the audience app, not a formatting choice.
 *
 * @param baseUrl  Audience origin, with or without a trailing slash.
 * @param roomCode The room's code.
 */
export function buildJoinUrl(baseUrl: string, roomCode: string): string {
  const origin = baseUrl.replace(/\/+$/, '');
  return `${origin}/?roomCode=${encodeURIComponent(roomCode)}`;
}

/**
 * The open mic's sign-up page, `/openmic/:eventId` on the audience app: the
 * code on the host's Sign-up screen and on a printed open mic sign. Keyed on
 * the EVENT, so it works before the show, when there is no Room to join yet.
 */
export function buildOpenMicSignUpUrl(baseUrl: string, eventId: string): string {
  const origin = baseUrl.replace(/\/+$/, '');
  return `${origin}/openmic/${encodeURIComponent(eventId)}`;
}
